import { useState } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import type { ExtensionInstance } from '@mangovoice/types'

import type { SwitchboardApi } from '../../api/switchboardApi'
import type { SwitchboardTranslateFn } from '../../types'
import { getSwitchboardDisplayName } from '../../utils/extensions'
import { text } from '../../utils/text'
import { EditSwitchboardMetadataDialog } from './EditSwitchboardMetadataDialog'
import { useSwitchboardMetadata } from './hooks'
import { SwitchboardCardContent } from './SwitchboardCardContent'
import { SwitchboardDeleteDialog } from './SwitchboardDeleteDialog'

export interface SwitchboardCardProps {
  /** Switchboard data */
  switchboard: { extension_dial_plan: number; updated_at: string }
  /** Extension instance data */
  extension: ExtensionInstance | undefined
  /** PBX ID */
  pbxId: number | null
  /** Callback when delete is triggered */
  onDelete?: (extensionDialPlan: number) => void
  /** Whether user can delete switchboards */
  canDelete?: boolean
  /** Whether delete operation is in progress */
  isDeleting: boolean
  /** Callback when card is clicked for navigation */
  onNavigate?: (extensionDialPlan: number) => void
  /** Route used for native browser link navigation */
  navigationHref?: string
  /** Switchboard API instance for metadata updates */
  api: SwitchboardApi
  /** Optional toast function for notifications */
  toast?: ToastLike
  /** Callback after successful metadata update */
  onMetadataUpdated?: () => void
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
}

/**
 * Switchboard card component with edit/delete functionality
 */
export const SwitchboardCard: React.FC<SwitchboardCardProps> = ({
  switchboard,
  extension,
  onDelete,
  canDelete = true,
  isDeleting,
  onNavigate,
  navigationHref,
  api,
  toast,
  onMetadataUpdated,
  t,
}) => {
  const [isMetadataDialogOpen, setIsMetadataDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const displayName = extension
    ? getSwitchboardDisplayName(extension)
    : text(
        t,
        'switchboard.list.common.unnamedSwitchboard',
        'Unnamed Switchboard',
      )

  const lastUpdated = switchboard?.updated_at
    ? new Date(switchboard.updated_at)
    : extension?.updated_at
      ? new Date(extension.updated_at)
      : new Date()

  const metadataForm = useSwitchboardMetadata({
    switchboard,
    extension,
    isEditDialogOpen: isMetadataDialogOpen,
    api,
    toast,
    t,
    onSuccess: () => {
      setIsMetadataDialogOpen(false)
      onMetadataUpdated?.()
    },
  })

  const handleDelete = () => {
    onDelete?.(switchboard.extension_dial_plan)
    setIsDeleteDialogOpen(false)
  }

  const handleNavigate = () => {
    onNavigate?.(switchboard.extension_dial_plan)
  }

  return (
    <>
      <SwitchboardCardContent
        displayName={displayName}
        lastUpdated={lastUpdated}
        extensionDialPlan={switchboard.extension_dial_plan}
        onEdit={() => setIsMetadataDialogOpen(true)}
        onDelete={
          canDelete && onDelete ? () => setIsDeleteDialogOpen(true) : undefined
        }
        canDelete={canDelete}
        isDeleting={isDeleting}
        onNavigate={onNavigate ? handleNavigate : undefined}
        navigationHref={navigationHref}
        t={t}
      />

      <EditSwitchboardMetadataDialog
        isOpen={isMetadataDialogOpen}
        onClose={() => setIsMetadataDialogOpen(false)}
        name={metadataForm.name}
        setName={metadataForm.setName}
        number={metadataForm.number}
        setNumber={metadataForm.setNumber}
        omitFromDirectory={metadataForm.omitFromDirectory}
        setOmitFromDirectory={metadataForm.setOmitFromDirectory}
        onSubmit={metadataForm.validateAndSubmit}
        isPending={metadataForm.isPending}
        t={t}
      />

      {canDelete && onDelete && (
        <SwitchboardDeleteDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          displayName={displayName}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
          t={t}
        />
      )}
    </>
  )
}
